import { useState, useEffect } from 'react';
import { Plus, Pencil, Trash2, Star, Quote } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle,
  DialogFooter
} from '../components/ui/dialog';
import { 
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { toast } from 'sonner';
import { getTestimonials, createTestimonial, updateTestimonial, deleteTestimonial } from '../lib/api';

const emptyTestimonial = {
  name: '',
  text: '',
  text_es: '',
  rating: 5,
  image_url: '',
  active: true
};

export default function AdminTestimonials() {
  const [testimonials, setTestimonials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingTestimonial, setEditingTestimonial] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const [formData, setFormData] = useState(emptyTestimonial);

  const fetchTestimonials = async () => {
    try {
      const response = await getTestimonials();
      setTestimonials(response.data);
    } catch (error) {
      toast.error('Error loading testimonials');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTestimonials();
  }, []);

  const openCreate = () => {
    setFormData(emptyTestimonial);
    setEditingTestimonial('new');
  };

  const openEdit = (testimonial) => {
    setFormData(testimonial);
    setEditingTestimonial(testimonial.id);
  };

  const handleSave = async () => {
    try {
      if (editingTestimonial === 'new') {
        await createTestimonial(formData);
        toast.success('Testimonial created');
      } else {
        await updateTestimonial(editingTestimonial, formData);
        toast.success('Testimonial updated');
      }
      setEditingTestimonial(null);
      fetchTestimonials();
    } catch (error) {
      toast.error('Error saving testimonial');
    }
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    try {
      await deleteTestimonial(deleteId);
      toast.success('Testimonial deleted');
      setDeleteId(null);
      fetchTestimonials();
    } catch (error) {
      toast.error('Error deleting testimonial');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-pulse text-slate-400">Loading testimonials...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-testimonials">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold text-white lg:hidden">Testimonials</h1>
        <Button 
          onClick={openCreate}
          className="bg-gradient-to-r from-orange-600 to-yellow-500 text-black font-bold"
          data-testid="add-testimonial-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          Add Testimonial
        </Button>
      </div>

      {/* Testimonials Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {testimonials.map((testimonial) => (
          <div 
            key={testimonial.id} 
            className={`admin-card p-6 ${!testimonial.active && 'opacity-60'}`}
            data-testid={`testimonial-card-${testimonial.id}`}
          >
            <div className="flex justify-between items-start mb-4">
              <div className="flex items-center gap-3">
                {testimonial.image_url ? (
                  <img 
                    src={testimonial.image_url} 
                    alt={testimonial.name}
                    className="w-12 h-12 rounded-full object-cover"
                  />
                ) : (
                  <div className="w-12 h-12 rounded-full bg-cyan-500/20 flex items-center justify-center">
                    <span className="text-cyan-400 font-bold text-lg">
                      {testimonial.name.charAt(0)}
                    </span>
                  </div>
                )}
                <div>
                  <h3 className="font-semibold text-white">{testimonial.name}</h3>
                  <div className="flex gap-0.5">
                    {[...Array(5)].map((_, i) => (
                      <Star 
                        key={i} 
                        className={`w-3 h-3 ${
                          i < testimonial.rating 
                            ? 'text-yellow-400 fill-yellow-400' 
                            : 'text-slate-600'
                        }`}
                      />
                    ))}
                  </div>
                </div>
              </div>
              <span className={`text-xs px-2 py-1 rounded-full ${
                testimonial.active 
                  ? 'bg-green-500/20 text-green-400' 
                  : 'bg-red-500/20 text-red-400'
              }`}>
                {testimonial.active ? 'Active' : 'Inactive'}
              </span>
            </div>

            <p className="text-sm text-slate-400 italic mb-4 line-clamp-3">
              "{testimonial.text}"
            </p>

            <div className="flex gap-2">
              <Button
                size="sm"
                variant="outline"
                className="flex-1 border-white/10 text-white hover:bg-white/5"
                onClick={() => openEdit(testimonial)}
              >
                <Pencil className="w-4 h-4 mr-2" />
                Edit
              </Button>
              <Button
                size="sm"
                variant="outline"
                className="border-red-500/50 text-red-400 hover:bg-red-500/10"
                onClick={() => setDeleteId(testimonial.id)}
              >
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>
          </div>
        ))}
      </div>

      {testimonials.length === 0 && (
        <div className="admin-card p-12 text-center">
          <Quote className="w-12 h-12 text-slate-600 mx-auto mb-4" />
          <p className="text-slate-400">No testimonials found</p>
        </div>
      )}

      {/* Edit/Create Dialog */}
      <Dialog open={!!editingTestimonial} onOpenChange={() => setEditingTestimonial(null)}>
        <DialogContent className="bg-[#1E293B] border-white/10 text-white max-w-lg">
          <DialogHeader>
            <DialogTitle>
              {editingTestimonial === 'new' ? 'Add New Testimonial' : 'Edit Testimonial'}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 max-h-[60vh] overflow-y-auto">
            <div className="space-y-2">
              <label className="text-sm text-slate-400">Customer Name</label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="bg-white/5 border-white/10 text-white"
                placeholder="John Doe"
                data-testid="testimonial-name-input"
              />
            </div>

            <div className="space-y-2">
              <label className="text-sm text-slate-400">Review Text (EN)</label>
              <Textarea
                value={formData.text}
                onChange={(e) => setFormData({ ...formData, text: e.target.value })}
                className="bg-white/5 border-white/10 text-white"
                placeholder="Excellent service..."
                data-testid="testimonial-text-input"
              />
            </div>

            <div className="space-y-2">
              <label className="text-sm text-slate-400">Review Text (ES)</label>
              <Textarea
                value={formData.text_es || ''}
                onChange={(e) => setFormData({ ...formData, text_es: e.target.value })}
                className="bg-white/5 border-white/10 text-white"
                placeholder="Excelente servicio..."
              />
            </div>

            <div className="space-y-2">
              <label className="text-sm text-slate-400">Rating</label>
              <div className="flex gap-2">
                {[1, 2, 3, 4, 5].map((rating) => (
                  <button
                    key={rating}
                    type="button"
                    onClick={() => setFormData({ ...formData, rating })}
                    className="p-2"
                  >
                    <Star 
                      className={`w-6 h-6 ${
                        rating <= formData.rating 
                          ? 'text-yellow-400 fill-yellow-400' 
                          : 'text-slate-600'
                      }`}
                    />
                  </button>
                ))}
              </div>
            </div>

            <div className="space-y-2">
              <label className="text-sm text-slate-400">Photo URL (optional)</label>
              <Input
                value={formData.image_url || ''}
                onChange={(e) => setFormData({ ...formData, image_url: e.target.value })}
                className="bg-white/5 border-white/10 text-white"
                placeholder="https://..."
              />
              {formData.image_url && (
                <img 
                  src={formData.image_url} 
                  alt="Preview" 
                  className="w-16 h-16 rounded-full object-cover mt-2"
                />
              )}
            </div>

            <div className="flex items-center justify-between">
              <label className="text-sm text-slate-400">Active</label>
              <Switch
                checked={formData.active}
                onCheckedChange={(checked) => setFormData({ ...formData, active: checked })}
              />
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setEditingTestimonial(null)}
              className="border-white/10 text-white hover:bg-white/5"
            >
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              className="bg-cyan-600 hover:bg-cyan-700"
              data-testid="save-testimonial-btn"
            >
              Save Testimonial
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent className="bg-[#1E293B] border-white/10">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-white">Delete Testimonial?</AlertDialogTitle>
            <AlertDialogDescription className="text-slate-400">
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-white/5 border-white/10 text-white hover:bg-white/10">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDelete}
              className="bg-red-600 hover:bg-red-700"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
